import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useAuth } from "../../../contexts/AuthContext";
import PageLayout from "../../../components/modern/PageLayout/PageLayout";
import { CalculatorForm } from "../order/components";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./ShipForMeCalculator.scss";

export const ShipForMeCalculator = () => {
  const { t } = useTranslation(['shipforme', 'common']);
  const { direction, isRTL } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<any>({
    link: "",
    merchantName: "",
    description: "",
    quantity: 1,
    width: 0,
    height: 0,
    length: 0,
    weight: 0,
    extendedDescription: "",
    destinationCountryId: 0,
    originCountryId: 0,
    paymentMethod: 0,
    categoryId: 0,
    shippingCompanyId: 0,
    merchentDeliveryFee: 0,
    itemType: 0,
    deliveryMode: 0,
    itemPrice: 0,
    // paymentMethodId: 0,
    paymentType: 2,
    shippingCompanyPricePerLB: 0,
    currencyId: 0,
    hasShippingInssurance: false,
    dgFees: 0,
    enableDGFees: false,
    cityId: 0,
    weightMeasurementUnitId: 0,
    lengthMeasurementUnitId: 0
  });

  const [countries, setCountries] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [shippingCompanies, setShippingCompanies] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [weightMeasurementUnits, setWeightMeasurementUnits] = useState<any[]>([]);
  const [lengthMeasurementUnits, setLengthMeasurementUnits] = useState<any[]>([]);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [isLBPriceEnabled, setIsLBPriceEnabled] = useState(false);

  useEffect(() => {
    loadDropdownData();
  }, []);

  // Re-fetch shipping companies when origin or destination country changes
  useEffect(() => {
    if (formData.originCountryId || formData.destinationCountryId) {
      loadShippingCompanies();
    }
  }, [formData.originCountryId, formData.destinationCountryId]);

  const loadShippingCompanies = async () => {
    try {
      // Build query parameters with filters
      const params = new URLSearchParams();
      params.append('pageSize', '10000');
      params.append('isActive', 'true');

      if (formData.originCountryId && formData.originCountryId > 0) {
        params.append('sourceCountryId', formData.originCountryId.toString());
      }

      if (formData.destinationCountryId && formData.destinationCountryId > 0) {
        params.append('destinationCountryId', formData.destinationCountryId.toString());
      }

      const shippingRes = await adminApi.call(`/shippingCompany?${params.toString()}`, 'GET');
      if (shippingRes.success) {
        setShippingCompanies(shippingRes.data?.items || []);
      }
    } catch (error) {
      console.error('Error loading shipping companies:', error);
    }
  };

  const loadDropdownData = async () => {
    setLoading(true);
    try {
      // Load countries
      const countriesRes = await adminApi.call('/country', 'GET');
      if (countriesRes.success) {
        setCountries(countriesRes.data?.items?.filter((x: any) => !x.isSuspended) || []);
      }

      // Load currencies
      const currenciesRes = await adminApi.call('/currency/GetBothAndSourceCurrencies?pageSize=1000000', 'GET');
      if (currenciesRes.success) {
        setCurrencies(currenciesRes.data?.items || []);
      }

      // Load categories
      const categoriesRes = await adminApi.call('/category?pageSize=10000', 'GET');
      if (categoriesRes.success) {
        setCategories(categoriesRes.data?.items || []);
      }

      // Load shipping companies
      const shippingRes = await adminApi.call('/shippingCompany?pageSize=10000&isActive=true', 'GET');
      if (shippingRes.success) {
        setShippingCompanies(shippingRes.data?.items || []);
      }

      // Load cities
      const citiesRes = await adminApi.call('/city?page=1&pageSize=1000', 'GET');
      if (citiesRes.success) {
        setCities(citiesRes.data?.items || []);
      }

      // Load payment methods
      const paymentRes = await adminApi.call('/paymentmethod?pageSize=10000', 'GET');
      if (paymentRes.success) {
        setPaymentMethods(paymentRes.data?.items || []);
      }

      // Load weight measurement units (type 1)
      const weightMeasurementRes = await adminApi.call('/measurementunit/by-type/1', 'GET');
      if (weightMeasurementRes.success) {
        const weightUnits = weightMeasurementRes.data || [];
        setWeightMeasurementUnits(weightUnits);
        // Set default weight measurement unit
        const defaultWeightUnit = weightUnits.find((u: any) => u.isDefault);
        if (defaultWeightUnit) {
          setFormData((prev: any) => ({ ...prev, weightMeasurementUnitId: defaultWeightUnit.id }));
        }
      }

      // Load length measurement units (type 0)
      const lengthMeasurementRes = await adminApi.call('/measurementunit/by-type/0', 'GET');
      if (lengthMeasurementRes.success) {
        const lengthUnits = lengthMeasurementRes.data || [];
        setLengthMeasurementUnits(lengthUnits);
        // Set default length measurement unit
        const defaultLengthUnit = lengthUnits.find((u: any) => u.isDefault);
        if (defaultLengthUnit) {
          setFormData((prev: any) => ({ ...prev, lengthMeasurementUnitId: defaultLengthUnit.id }));
        }
      }

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      showErrorToast('Error loading form data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: any) => {
    const { name, value, type, checked } = event.target;

    // Handle checkbox inputs differently
    if (type === "checkbox") {
      setFormData((prev: any) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]:
        name.includes("CountryId") ||
          name.includes("categoryId") ||
          name.includes("Type") ||
          name.includes("deliveryMode") ||
          name.includes("MeasurementUnitId") ||
          name.includes("shippingCompanyId") ||
          name.includes("currencyId") ||
          name.includes("paymentMethodId") ||
          name.includes("cityId") ||
          name === "initialPartialPayment" ||
          name === "quantity" ||
          name === "itemPrice" ||
          name === "merchentDeliveryFee" ||
          name === "width" ||
          name === "height" ||
          name === "length" ||
          name === "weight" ||
          name === "shippingCompanyPricePerLB"
          ? Number(value)
          : value,
    }));
  };

  const handleQuantityChange = (increment: number) => {
    setFormData((prev: any) => ({
      ...prev,
      quantity: Math.max(1, prev.quantity + increment)
    }));
  };

  const calculateOrder = async () => {
    setCalculating(true);
    setResult(null);

    try {
      const response = await adminApi.call('/adminshipforme/calculate-shipforme', 'POST', formData);

      if (response.success) {
        setResult(response.data);
        showSuccessToast(t('common:messages.calculationSuccess'));
      } else {
        showErrorToast(response.error || t('common:messages.calculationFailed'));
      }
    } catch (error) {
      showErrorToast(t('common:messages.networkError'));
      console.error('Calculation error:', error);
    } finally {
      setCalculating(false);
    }
  };

  const resetForm = () => {
    setResetting(true);
    setFormData({
      link: "",
      merchantName: "",
      description: "",
      quantity: 1,
      width: 0,
      height: 0,
      length: 0,
      weight: 0,
      extendedDescription: "",
      destinationCountryId: 0,
      originCountryId: 0,
      paymentMethod: 0,
      categoryId: 0,
      shippingCompanyId: 0,
      merchentDeliveryFee: 0,
      itemType: 0,
      deliveryMode: 0,
      itemPrice: 0,
      // paymentMethodId: 0,
      paymentType: 0,
      shippingCompanyPricePerLB: 0,
      currencyId: 0,
      hasShippingInssurance: false,
      dgFees: 0,
      enableDGFees: false,
      cityId: 0,
      weightMeasurementUnitId: 0,
      lengthMeasurementUnitId: 0
    });
    setResult(null);
    setTimeout(() => setResetting(false), 200);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const breadcrumbs = [
    { path: '/shipforme', label: t('shipforme:breadcrumbs.shipforme') },
    { label: t('shipforme:calculator.title') }
  ];

  return (
    <PageLayout
      title={t('shipforme:calculator.title')}
      subtitle={t('shipforme:calculator.subtitle')}
      breadcrumbs={breadcrumbs}

    >
      <div className="card">
        <div className="card-body">
          <CalculatorForm
            formData={formData}
            countries={countries}
            currencies={currencies}
            categories={categories}
            shippingCompanies={shippingCompanies}
            cities={cities}
            paymentMethods={paymentMethods}
            weightMeasurementUnits={weightMeasurementUnits}
            lengthMeasurementUnits={lengthMeasurementUnits}
            result={result}
            isLBPriceEnabled={isLBPriceEnabled}
            setIsLBPriceEnabled={setIsLBPriceEnabled}
            handleInputChange={handleInputChange}
            handleQuantityChange={handleQuantityChange}
            calculateOrder={calculateOrder}
            resetForm={resetForm}
            calculating={calculating}
            resetting={resetting}
            type="shipforme"
          />
        </div>
      </div>
    </PageLayout>
  );
};
